/**
 * Tenant Dashboard — the landing overview for every tenant user.
 *
 * One request (GET /admin/dashboard/overview) → KPI cards per module, a live
 * "needs attention" strip, the newest conversations, and quick actions. Every
 * card is a link into its module, and cards for modules the current user
 * cannot access are hidden (hasModuleAccess), so the page is safe to show to
 * every role — it never reveals a module the sidebar wouldn't.
 */
import PageHeader from '@/components/ui/PageHeader';
import StatCard from '@/components/ui/StatCard';
import { useAuth } from '@/hooks/useAuth';
import { useModuleAccess } from '@/hooks/useModuleAccess';
import adminApi, { DashboardOverview } from '@/services/admin.api';
import {
  AccountTree,
  Campaign,
  ChatBubble,
  Event,
  Language,
  Message,
  NotificationsActive,
  People,
  SmartToy,
  Schedule,
  SupportAgent,
  TableView,
  WhatsApp,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Avatar,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasModuleAccess } = useModuleAccess();
  const { tenant } = useAuth();
  const tenantName: string = (tenant as any)?.name || '';
  // Live clock for the header (minute resolution is enough; re-render every 30s)
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(id);
  }, []);
  // Same rule as AdminLayout: the app is mounted under both /admin and /.
  const pathPrefix = location.pathname.startsWith('/admin') ? '/admin' : '';
  const [data, setData] = useState<DashboardOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    adminApi
      .getDashboardOverview()
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: any) => {
        if (!cancelled) setError(e?.response?.data?.error?.message || 'Failed to load dashboard');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const go = (path: string) => () => navigate(`${pathPrefix}${path}`);

  // Each KPI names the module that owns it — hidden when the user lacks it.
  const kpis = [
    {
      module: 'activity.chatLogs',
      title: 'Active Chats',
      value: data?.conversations.active ?? 0,
      subtitle: `${data?.conversations.today ?? 0} started today`,
      icon: <ChatBubble />,
      color: '#4f46e5',
      path: '/activity/chat-logs',
    },
    {
      module: 'activity.chatLogs',
      title: 'Messages Today',
      value: data?.messages.today ?? 0,
      subtitle: `${data?.conversations.total ?? 0} conversations total`,
      icon: <Message />,
      color: '#0ea5e9',
      path: '/activity/chat-logs',
    },
    {
      module: 'activity.leads',
      title: 'New Leads',
      value: data?.leads.new ?? 0,
      subtitle: `${data?.leads.today ?? 0} today · ${data?.leads.total ?? 0} total`,
      icon: <People />,
      color: '#10b981',
      path: '/activity/leads',
    },
    {
      module: 'appointments',
      title: 'Appointments',
      value: data?.appointments.requested ?? 0,
      subtitle: `${data?.appointments.upcoming7d ?? 0} confirmed in next 7 days`,
      icon: <Event />,
      color: '#f59e0b',
      path: '/appointments',
    },
    {
      module: 'campaigns',
      title: 'Active Campaigns',
      value: data?.campaigns.active ?? 0,
      subtitle: `${data?.campaigns.sendsToday ?? 0} messages sent today`,
      icon: <Campaign />,
      color: '#8b5cf6',
      path: '/campaigns',
    },
    {
      module: 'automation',
      title: 'Automations',
      value: data?.automation.active ?? 0,
      subtitle: `active · ${data?.automation.total ?? 0} total · ${data?.automation.runsToday ?? 0} runs today`,
      icon: <AccountTree />,
      color: '#14b8a6',
      path: '/automation/workflows',
    },
    {
      module: 'whatsapp',
      title: 'Templates',
      value: data?.templates.approved ?? 0,
      subtitle: `${data?.templates.draft ?? 0} drafts · ${data?.templates.total ?? 0} total`,
      icon: <WhatsApp />,
      color: '#22c55e',
      path: '/whatsapp-templates',
    },
    {
      module: 'leadForm',
      title: 'Web Chats',
      value: data?.webChats.activeToday ?? 0,
      subtitle: `active today · ${data?.webChats.total ?? 0} sessions total`,
      icon: <Language />,
      color: '#ec4899',
      path: '/web-chats',
    },
  ].filter((k) => hasModuleAccess(k.module));

  const needsHuman = data?.conversations.needsHuman ?? 0;
  const alerts = data?.alerts.unresolved ?? 0;
  const showAttention =
    (needsHuman > 0 && hasModuleAccess('activity.chatLogs')) ||
    (alerts > 0 && hasModuleAccess('systemAlerts'));

  const quickActions = [
    { module: 'campaigns', label: 'New Campaign', icon: <Campaign />, path: '/campaigns/new' },
    { module: 'whatsapp', label: 'New Template', icon: <WhatsApp />, path: '/whatsapp-templates/new' },
    { module: 'automation', label: 'Build Automation', icon: <AccountTree />, path: '/automation/workflows' },
    { module: 'agents', label: 'Manage Agents', icon: <SmartToy />, path: '/agents' },
    { module: 'leadForm', label: 'Add Web Chat', icon: <Language />, path: '/web-chats' },
    { module: 'reports', label: 'Export Reports', icon: <TableView />, path: '/reports' },
  ].filter((a) => hasModuleAccess(a.module));

  return (
    <Box>
      <PageHeader
        title={tenantName || 'Dashboard'}
        subtitle={tenantName ? "Dashboard · What's happening across your account today" : "What's happening across your account today"}
        actions={
          <Chip
            icon={<Schedule />}
            variant="outlined"
            label={`${now.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })} · ${now.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`}
            sx={{ fontWeight: 500 }}
          />
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Needs attention — only when there is something to act on */}
      {!loading && showAttention && (
        <Stack direction="row" spacing={2} mb={3} flexWrap="wrap" useFlexGap>
          {needsHuman > 0 && hasModuleAccess('activity.chatLogs') && (
            <Alert
              severity="warning"
              icon={<SupportAgent />}
              action={
                <Button color="inherit" size="small" onClick={go('/activity/chat-logs')}>
                  Open inbox
                </Button>
              }
              sx={{ flex: 1, minWidth: 280 }}
            >
              <strong>{needsHuman}</strong> conversation{needsHuman === 1 ? '' : 's'} waiting for a human
            </Alert>
          )}
          {alerts > 0 && hasModuleAccess('systemAlerts') && (
            <Alert
              severity="error"
              icon={<NotificationsActive />}
              action={
                <Button color="inherit" size="small" onClick={go('/system-alerts')}>
                  View alerts
                </Button>
              }
              sx={{ flex: 1, minWidth: 280 }}
            >
              <strong>{alerts}</strong> unresolved system alert{alerts === 1 ? '' : 's'}
            </Alert>
          )}
        </Stack>
      )}

      <Grid container spacing={3}>
        {kpis.map((k) => (
          <Grid item xs={12} sm={6} lg={4} xl={3} key={k.title}>
            <Box onClick={go(k.path)} sx={{ cursor: 'pointer', height: '100%' }}>
              <StatCard
                title={k.title}
                value={loading ? '—' : k.value}
                {...(loading ? {} : { subtitle: k.subtitle })}
                icon={k.icon}
                color={k.color}
                loading={loading}
              />
            </Box>
          </Grid>
        ))}

        {/* Recent conversations */}
        {hasModuleAccess('activity.chatLogs') && (
          <Grid item xs={12} md={8}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                  <Typography variant="h6">Recent Conversations</Typography>
                  <Button size="small" onClick={go('/activity/chat-logs')}>
                    View all
                  </Button>
                </Box>
                <Divider sx={{ mb: 1 }} />
                {loading ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    {Array.from({ length: 4 }).map((_, i) => (
                      <Box key={i} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Skeleton variant="circular" width={36} height={36} />
                        <Box sx={{ flex: 1 }}>
                          <Skeleton width="55%" height={16} sx={{ mb: 0.75 }} />
                          <Skeleton width="80%" height={14} />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : !data?.recentConversations.length ? (
                  <Box sx={{ py: 5, textAlign: 'center', color: 'text.secondary' }}>
                    <ChatBubble sx={{ fontSize: 40, opacity: 0.2, mb: 1 }} />
                    <Typography variant="body2">No conversations yet</Typography>
                  </Box>
                ) : (
                  <List dense disablePadding>
                    {data.recentConversations.map((c) => (
                      <ListItemButton
                        key={c._id}
                        onClick={() => navigate(`${pathPrefix}/activity/chat-logs?conversationId=${c._id}`)}
                        sx={{ borderRadius: 1.5 }}
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36, fontSize: 15 }}>
                            {(c.customerName || c.customerPhone || '?').charAt(0).toUpperCase()}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="body2" fontWeight={600} noWrap>
                                {c.customerName || c.customerPhone}
                              </Typography>
                              {c.routingState !== 'bot' && (
                                <Chip
                                  size="small"
                                  color={c.routingState === 'pending_human' ? 'warning' : 'default'}
                                  label={c.routingState === 'pending_human' ? 'Needs human' : 'Human'}
                                  sx={{ height: 18, fontSize: 10 }}
                                />
                              )}
                              {c.unreadCount > 0 && (
                                <Chip size="small" color="primary" label={c.unreadCount} sx={{ height: 18, fontSize: 10 }} />
                              )}
                            </Box>
                          }
                          secondary={c.lastMessage || `${c.channel} · ${c.status}`}
                          secondaryTypographyProps={{ noWrap: true }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1, whiteSpace: 'nowrap' }}>
                          {c.lastMessageAt ? new Date(c.lastMessageAt).toLocaleString() : ''}
                        </Typography>
                      </ListItemButton>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Quick actions */}
        {quickActions.length > 0 && (
          <Grid item xs={12} md={hasModuleAccess('activity.chatLogs') ? 4 : 12}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" mb={1.5}>
                  Quick Actions
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={1.25}>
                  {quickActions.map((a) => (
                    <Button
                      key={a.label}
                      variant="outlined"
                      startIcon={a.icon}
                      onClick={go(a.path)}
                      sx={{ justifyContent: 'flex-start' }}
                      fullWidth
                    >
                      {a.label}
                    </Button>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default Dashboard;
